"use client";

import { Button } from "@/components/ui/button";
import { bulkActionCounts, bulkSelectionLabel, type BulkAction } from "@/lib/host-panel/catalog-mutations";
import type { ListedPiece } from "@/lib/host-panel/catalog-types";
import { PANEL_LAYER_CLASS } from "@/lib/host-panel/layer-stack";
import { cn } from "@/lib/utils";

/**
 * Bulk selection bar (handoff §7.6, criteria 17.16 and 17.17), pinned to the foot in add scope.
 *
 * Each action appears only if it would have an effect, because a button that changes nothing
 * invites discovering that by trial. The region is live, so the count is heard rather than hunted.
 */
export function BulkBar({
  pieces,
  selection,
  onApply,
  onClear,
}: {
  pieces: readonly ListedPiece[];
  selection: ReadonlySet<string>;
  onApply: (action: BulkAction) => void;
  onClear: () => void;
}) {
  const counts = bulkActionCounts(pieces, selection);
  // One source of truth: the label and the bar's very existence read the same total the buttons
  // do, the ids that resolve to a piece. A raw `selection.size` also counts ids that match no
  // piece (a revalidated catalogue dropped it, the list changed elsewhere), and the bar would
  // announce pieces it cannot act on. If this is zero, there is nothing to draw.
  const actionable = counts.add + counts.remove;

  if (actionable === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        PANEL_LAYER_CLASS.bulkBar,
        // Above whatever already occupies the foot of the panel — the tab bar, on a phone
        // (`--tb-panel-bottom-base`, the prototype's `base-inferior`, which it passes to the
        // catalog exactly for this). With `bottom-0` this bar sat **on top** of the tab bar and
        // the five sections of the panel became unreachable while a piece was selected.
        "fixed inset-x-0 bottom-[var(--tb-panel-bottom-base,0px)] flex flex-wrap items-center justify-between gap-4 border-t border-[var(--tb-border)] bg-[rgb(251_247_244_/_0.96)] px-[clamp(20px,4vw,56px)] py-3.5 [backdrop-filter:blur(14px)]",
        // Enters rising, like the mode control it covers: a bar that appears finished at the
        // foot of the screen reads as a paint failure.
        "motion-safe:animate-[tb-flow-in_var(--tb-duration-3)_var(--tb-ease-linen)_both]",
      )}
    >
      <span className="flex min-w-0 items-center gap-3.5">
        <span className="font-mono text-[12.5px] tabular-nums text-[var(--fg)]">
          {bulkSelectionLabel(actionable)}
        </span>
        <button
          type="button"
          onClick={onClear}
          // A text link, as drawn: one line with a rule under it. Its 44px box made the bar taller
          // than the prototype's and lifted everything in it.
          className="cursor-pointer border-b border-[var(--tb-border)] text-[12.5px] leading-[normal] text-[var(--fg-muted)]"
        >
          Limpar seleção
        </button>
      </span>

      <span className="flex flex-wrap items-center gap-2.5">
        {counts.remove > 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => onApply("remove")}
            className="px-5 py-[13px] font-bold leading-[normal]"
          >
            Remover {counts.remove}
          </Button>
        ) : null}

        {counts.add > 0 ? (
          <Button
            type="button"
            onClick={() => onApply("add")}
            className="px-[22px] py-[13px] font-bold leading-[normal]"
          >
            Adicionar {counts.add}
          </Button>
        ) : null}
      </span>
    </div>
  );
}
